/**
 * Flag Utilities
 * Convenience functions for flag lookups with flag_code and optimized MMSI fallback
 */

import type { FlagData } from '@/services/flags/FlagLibrary';
import { flagLibrary } from '@/services/flags/FlagLibrary';
import type { Vessel } from '../types/vessels.types';

/**
 * Get flag from vessel data
 * Uses flag_code (primary) or MMSI prefix (fallback)
 * Returns default flag if both are missing or invalid
 *
 * @param vessel - Vessel object with optional flag_code and mmsi
 * @returns Flag data (always returns a flag, default if needed)
 */
export const getFlagFromVessel = (vessel: Vessel | { flag_code?: string; mmsi?: string | number }): FlagData => {
  return flagLibrary.getFlag(vessel.flag_code, vessel.mmsi);
};

/**
 * Get flag from vessel's flag_code with optimized MMSI prefix fallback
 * Returns default flag if flag_code is missing or invalid
 *
 * @param flagCode - Flag code from vessel (e.g., "BAH", "PAN")
 * @param mmsi - Optional MMSI for fallback lookup
 * @returns Flag data (always returns a flag, default if needed)
 */
export const getFlag = (flagCode: string | undefined | null, mmsi?: string | number | undefined | null): FlagData => {
  return flagLibrary.getFlag(flagCode, mmsi);
};

/**
 * Get flag by flag_code (returns undefined if not found)
 *
 * @param flagCode - Flag code (e.g., "BAH", "PAN")
 * @returns Flag data or undefined
 */
export const getFlagByCode = (flagCode: string | undefined | null): FlagData | undefined => {
  return flagLibrary.getByCode(flagCode);
};

/**
 * Get flag by MMSI prefix (returns undefined if not found)
 * Optimized O(1) lookup with validation
 *
 * @param mmsi - MMSI number or string
 * @returns Flag data or undefined
 */
export const getFlagByMMSI = (mmsi: string | number | undefined | null): FlagData | undefined => {
  return flagLibrary.getByMMSI(mmsi);
};

/**
 * Get default/unknown flag
 *
 * @returns Default flag data
 */
export const getDefaultFlag = (): FlagData => {
  return flagLibrary.getDefaultFlag();
};
